import { Button } from "@/components/ui/button";
import { ContentLoader } from "@/components/ui/loaders";
import { ActionBar } from "@/components/layout/app.layout";
import { SegmentedControl } from "@/components/fornada/controls";
import { ConfirmSheet } from "@/components/fornada/sheet";
import {
  BarRow,
  EmptyState,
  ScreenTitle,
  SectionHeader,
  StatCell,
  StatStrip,
  Surface,
} from "@/components/fornada/primitives";
import { useActiveSale, useFlavors } from "@/hooks/use-active-sale";
import { useSetSaleOrders } from "@/hooks/use-sale-orders";
import { date } from "@/lib/date";
import { formatQuantity, initials, unitLabel } from "@/lib/format";
import {
  type BarItem,
  byFlavor,
  byIngredient,
  bySeller,
  daysToClose,
  deadlineLabel,
  isOrdersOpen,
  pizzasOf,
} from "@/lib/sale";
import { useAuth } from "@/providers/auth.provider";
import { Download, Lock, LockOpen, Pencil, ShoppingCart } from "lucide-react";
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";

type Tab = "sabor" | "ingredientes" | "vendedores";

const openReport = (kind: "shopping-list" | "order-list", saleId: string) =>
  window.open(`/api/v1/reports/${kind}/${saleId}`, "_blank");

function BarList({ rows, people }: { rows: BarItem[]; people?: boolean }) {
  const top = Math.max(...rows.map((r) => r.value), 1);
  if (!rows.length)
    return <p className="px-[18px] py-6 text-center text-[13px] text-fx-faint">Nada por aqui ainda.</p>;
  return (
    <div className="divide-y divide-fx-line-subtle px-[18px]">
      {rows.map((r) => (
        <BarRow
          key={r.key}
          name={r.name}
          value={formatQuantity(r.value)}
          unit={r.unit ? unitLabel(r.unit) : undefined}
          initials={people ? initials(r.name) : undefined}
          percent={Math.round((r.value / top) * 100)}
          className="py-3"
        />
      ))}
    </div>
  );
}

const CampaignPage = () => {
  const { user } = useAuth();
  const { data: sale, isLoading } = useActiveSale();
  const { data: flavors = [] } = useFlavors();
  const [tab, setTab] = useState<Tab>("sabor");
  const [confirming, setConfirming] = useState(false);
  const setOrders = useSetSaleOrders();

  if (isLoading) return <ContentLoader show />;
  if (user?.role !== "ADMIN") return <Navigate to="/" replace />;

  if (!sale) {
    return (
      <div className="flex flex-col gap-7">
        <ScreenTitle title="Campanha" subtitle="Nenhuma campanha ativa no momento." />
        <EmptyState
          title="Nenhuma campanha ativa"
          description="Crie uma campanha para os vendedores começarem a registrar pedidos."
        >
          <Button asChild size="lg" className="mt-2">
            <Link to="/campaign/edit?new=1">Criar campanha</Link>
          </Button>
        </EmptyState>
      </div>
    );
  }

  const rows = {
    sabor: byFlavor(sale.orders),
    ingredientes: byIngredient(sale.orders, flavors),
    vendedores: bySeller(sale.orders),
  };
  const sellers = new Set(sale.orders.map((o) => o.userId)).size;
  const open = isOrdersOpen(sale);
  const days = daysToClose(sale);

  return (
    <div className="flex flex-col gap-6">
      <ScreenTitle
        accent
        eyebrow={`${deadlineLabel(sale)} · Entrega ${date(sale.endDate).format("DD/MM")}`}
        title={sale.name}
      >
        <Button
          asChild
          variant="quiet"
          size="icon"
          className="min-[960px]:hidden"
        >
          <Link to="/campaign/edit" aria-label="Editar campanha">
            <Pencil size={18} strokeWidth={1.6} />
          </Link>
        </Button>
        <div className="hidden gap-3 min-[960px]:flex">
          <Button asChild variant="quiet">
            <Link to="/campaign/edit">Editar campanha</Link>
          </Button>
          <Button variant="outline" onClick={() => openReport("order-list", sale.id)}>
            <Download /> Lista de pedidos
          </Button>
          <Button onClick={() => openReport("shopping-list", sale.id)}>
            <ShoppingCart /> Lista de compras
          </Button>
        </div>
      </ScreenTitle>

      <StatStrip className="[&>*]:min-w-0">
        <StatCell label="Pizzas" value={pizzasOf(sale.orders)} cream />
        <StatCell label="Pedidos" value={sale.orders.length} />
        <StatCell label="Vendedores" value={sellers} />
        <StatCell
          label={open ? "Fecha em" : "Pedidos"}
          value={open ? `${days} ${days === 1 ? "DIA" : "DIAS"}` : "FECHADOS"}
          className="hidden min-[960px]:flex"
        />
      </StatStrip>

      <Surface className="flex flex-wrap items-center gap-3 p-4">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-sm font-semibold text-fx-title">
            {open ? "Vendas abertas" : "Vendas fechadas"}
          </span>
          <span className="text-xs text-fx-faint">
            {open
              ? "Os vendedores podem registrar e alterar pedidos."
              : "Só administradores registram e alteram pedidos."}
          </span>
        </div>
        <Button
          variant={open ? "outline" : "default"}
          disabled={setOrders.isPending}
          onClick={() => setConfirming(true)}
        >
          {open ? <Lock /> : <LockOpen />}
          {open ? "Fechar vendas" : "Reabrir vendas"}
        </Button>
      </Surface>

      <SegmentedControl
        className="min-[960px]:hidden"
        value={tab}
        onChange={setTab}
        options={[
          { value: "sabor", label: "Por sabor" },
          { value: "ingredientes", label: "Ingredientes" },
          { value: "vendedores", label: "Vendedores" },
        ]}
      />

      {/* mobile: uma lista por vez */}
      <Surface className="py-1 min-[960px]:hidden">
        <BarList rows={rows[tab]} people={tab === "vendedores"} />
      </Surface>

      {/* desktop: três colunas */}
      <div className="hidden grid-cols-3 items-start gap-4 min-[960px]:grid">
        <section className="flex flex-col gap-3">
          <SectionHeader title="Por sabor" />
          <Surface className="overflow-hidden py-1">
            <BarList rows={rows.sabor} />
          </Surface>
        </section>
        <section className="flex flex-col gap-3">
          <SectionHeader title="Ingredientes" />
          <Surface className="overflow-hidden py-1">
            <BarList rows={rows.ingredientes} />
          </Surface>
        </section>
        <section className="flex flex-col gap-3">
          <SectionHeader title="Vendedores" />
          <Surface className="overflow-hidden py-1">
            <BarList rows={rows.vendedores} people />
          </Surface>
        </section>
      </div>

      <ActionBar aboveTabBar className="min-[960px]:hidden">
        <Button
          className="min-w-0 flex-1 px-3 [&_svg]:size-4"
          onClick={() => openReport("shopping-list", sale.id)}
        >
          <ShoppingCart /> Lista de compras
        </Button>
        <Button
          className="min-w-0 flex-1 px-3 [&_svg]:size-4"
          variant="outline"
          onClick={() => openReport("order-list", sale.id)}
        >
          <Download /> Lista de pedidos
        </Button>
      </ActionBar>

      <ConfirmSheet
        open={confirming}
        onOpenChange={setConfirming}
        title={open ? "Fechar vendas?" : "Reabrir vendas?"}
        description={
          open
            ? "Os vendedores deixam de registrar e alterar pedidos até você reabrir."
            : "Os vendedores voltam a registrar e alterar pedidos até você fechar as vendas. O prazo da campanha não fecha mais sozinho."
        }
        confirmLabel={open ? "Fechar vendas" : "Reabrir vendas"}
        destructive={open}
        pending={setOrders.isPending}
        onConfirm={() =>
          setOrders.mutate({ id: sale.id, open: !open }, { onSuccess: () => setConfirming(false) })
        }
      />
    </div>
  );
};

export default CampaignPage;
